import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Box, type BoxProps } from '@frontend/legacy-components';

import { GenericError } from 'modules/common/components/errorBoundary/GenericError';
import { useWebViewerContext } from 'modules/common/viewer/pdfViewer/WebViewerContext';
import { useAnnotationOnSelectCallbacks } from 'modules/common/viewer/pdfViewer/annotationCommands/useAnnotationOnSelectCallbacks';
import { useSuggestionAnnotations } from 'modules/common/viewer/pdfViewer/annotationCommands/useSuggestionAnnotations';

import ApplyRedactionsModal from './ApplyRedactionsModal';
import { buildSuggestionAnnotationClass } from './annotationCommands/SuggestionAnnotation';
import { CalloutAuthor } from './calloutAuthor/CalloutAuthor';
import { useCalloutAuthor } from './calloutAuthor/useCalloutAuthor';
import {
  useAnnotationEventListeners,
  useAnnotationInteractions,
  useCompareFilesCustomPanel,
  useCustomDownloadOptions,
  useCustomZoom,
  useExtractionHoverCursor,
  useInitialLeftPanelState,
  useInitializePDFViewer,
  useRedactFileOnRequest,
  useScaleMeasurements,
  useSearchQuery,
  useTextPopupButton,
  useManualTableSelection,
} from './hooks';
import type { ViewerConfig } from './viewerConfig';

type PDFViewerProps = {
  activeFileUuid: string | null;
  isPaused: boolean;
  filename: string | null;
  url: string | null;
  config: ViewerConfig;
} & Omit<BoxProps, 'children'>;

export const PDFViewer = ({
  activeFileUuid,
  isPaused,
  filename,
  url,
  config,
  ...boxProps
}: PDFViewerProps) => {
  const [isLoadedByUrl, setIsLoadedByUrl] = useState<Record<string, boolean>>(
    {},
  );

  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<
    Set<string>
  >(new Set());

  const { instance, attachWebViewer, viewerRef } = useWebViewerContext();
  const {
    extractionsEnabled,
    tableExtractionsEnabled,
    canDownloadPdf,
    readOnlyAnnotationImport,
    pdftron: { disabledElements, initialToolbarGroup },
  } = config;

  // NB: This is the only way we can make custom PDFTron classes, by defining them
  // as an extension of the actual webViewerInstance.Core.Annotations.CustomAnnotation
  // class. That object does not exist until runtime.
  const SuggestionAnnotationClass = useMemo(() => {
    if (instance?.Core.annotationManager) {
      return buildSuggestionAnnotationClass({
        webViewerInstance: instance,
        annotationManager: instance.Core.annotationManager,
      });
    }
    return null;
  }, [instance]);

  // on mount initialize webviewer
  useEffect(() => {
    if (viewerRef.current) {
      attachWebViewer(viewerRef.current);
    }
  }, [attachWebViewer, viewerRef]);

  // Core functionality hooks used to improve viewer functionality, unrelated to specific features
  useRedactFileOnRequest({ instance, url });

  useSearchQuery({ instance });

  useScaleMeasurements({ instance });

  // BEGIN CUSTOM TOOLS - order here matters
  useManualTableSelection({ instance, tableExtractionsEnabled });

  const { calloutAuthorInfo, handleCloseCalloutAuthor } = useCalloutAuthor({
    instance,
    url,
    extractionsEnabled,
  });
  // END CUSTOM TOOLS

  useAnnotationInteractions({
    isLoaded: isPaused ? false : isLoadedByUrl[url ?? ''] ?? false,
    instance,
    selectedAnnotationIds,
    readOnlyAnnotationImport,
  });

  useSuggestionAnnotations({
    isLoaded: isPaused ? false : isLoadedByUrl[url ?? ''] ?? false,
    annotationManager: instance?.Core.annotationManager ?? null,
    documentViewer: instance?.Core.documentViewer ?? null,
    webViewerInstance: instance,
    activeFileUuid,
    SuggestionAnnotationClass,
  });

  useInitializePDFViewer({
    instance,
    setIsLoadedByUrl,
    SuggestionAnnotationClass,
    disabledElements,
    initialToolbarGroup,
    canDownloadPdf,
  });

  useTextPopupButton({ instance });

  useAnnotationEventListeners({
    instance,
    selectedAnnotationIds,
    setSelectedAnnotationIds,
  });

  useCustomDownloadOptions({
    instance,
    filename,
    isLoaded: isPaused ? false : isLoadedByUrl[url ?? ''] ?? false,
    canDownloadPdf,
  });

  useExtractionHoverCursor({ instance });

  // setting up compare custom panel
  useCompareFilesCustomPanel({
    instance,
    filename,
    url,
  });

  useInitialLeftPanelState({ instance });

  // Core functionality: improves zoom behavior in PDFTron
  useCustomZoom({
    instance,
    isLoaded: isPaused ? false : isLoadedByUrl[url ?? ''] ?? false,
  });

  const shouldLoadDocumentUrl = useMemo(() => {
    return Boolean(url && !isLoadedByUrl[url]);
  }, [isLoadedByUrl, url]);

  useEffect(() => {
    if (instance && !isPaused && url && shouldLoadDocumentUrl) {
      const loadDocByUrl = async () => {
        // loadDocument can return undefined in our PDFTron versions, so wrap in
        // Promise.resolve to safely attach a catch. Benign cancellation errors
        // (rapid document switching) are suppressed globally in WebViewerContext.
        Promise.resolve(
          instance.UI.loadDocument(url, { documentId: url }),
        ).catch((err: unknown) => {
          console.error('loadDocument rejected unexpectedly:', err);
        });
      };

      loadDocByUrl();
    }
  }, [instance, isPaused, shouldLoadDocumentUrl, url]);

  useAnnotationOnSelectCallbacks({
    instance,
    annotationManager: instance?.Core.annotationManager ?? null,
    SuggestionAnnotationClass,
  });

  return (
    <Box {...boxProps}>
      <ErrorBoundary
        fallback={
          <GenericError size="lg" height="100%" justifyContent="center" />
        }
        onError={(error) =>
          console.error('Error occurred in PDFViewerContent', error)
        }
      >
        <Box height="100%" className="pdf-viewer-container" ref={viewerRef} />
      </ErrorBoundary>
      <ApplyRedactionsModal />
      <CalloutAuthor
        activeFileUuid={activeFileUuid}
        calloutAuthorInfo={calloutAuthorInfo}
        handleCloseCalloutAuthor={handleCloseCalloutAuthor}
      />
    </Box>
  );
};
